import Component from '@glimmer/component';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import { LinkTo } from '@ember/routing';
import { PhTable } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import {
  PRODUCT_LABELS,
  PRODUCT_SECTION_ORDER,
  VALID_REPORT_TYPES,
  REPORT_TYPE_FILTER_VALUE,
  type ProductCode,
} from '../utils/dataset-utils';
import type StoreService from 'ember-data/store';
import type Report from 'tio-data/models/reports';

interface PartnerReportsPageSignature {
  Args: {
    companyId: string;
    viewerRoute: string;
    /**
     * Optional product-type restriction (e.g. ["tas"]). When omitted, all
     * builder report types are shown. Used by product-scoped pages (the
     * Tuition Assistance reporting page shows only tas reports).
     */
    reportTypes?: string[];
  };
}

export default class PartnerReportsPage extends Component<PartnerReportsPageSignature> {
  @service declare store: StoreService;

  /**
   * Normalized product-type restriction from @reportTypes: lowercased and
   * intersected with the known builder types so casing ("TAS") or unknown
   * values can't silently mismatch the lowercase report_type values.
   * Null = no restriction. If every provided type is unknown, the set is
   * empty and nothing renders — failing narrow rather than widening partner
   * visibility on a bad call site.
   */
  get restrictedReportTypes(): Set<string> | null {
    const types = this.args.reportTypes;
    if (!types?.length) return null;
    return new Set(
      types.map((t) => t.trim().toLowerCase()).filter((t) => VALID_REPORT_TYPES.has(t))
    );
  }

  /** Server-side report_type filter value — restricted types or all builder products */
  get reportTypeFilter(): string {
    const restricted = this.restrictedReportTypes;
    return restricted?.size ? [...restricted].join(',') : REPORT_TYPE_FILTER_VALUE;
  }

  /** Client-side guard mirroring the server filter */
  get allowedReportTypes(): Set<string> {
    return this.restrictedReportTypes ?? VALID_REPORT_TYPES;
  }

  reportsData = trackedFunction(this, async () => {
    const results = await this.store.query<Report>('report', {
      page: { limit: 200 },
      filter: { report_type: this.reportTypeFilter },
    });
    return Array.from(results as Iterable<Report>);
  });

  get isLoading() {
    return this.reportsData.isLoading;
  }

  get publishedReports() {
    const all = this.reportsData.value ?? [];
    const companyId = Number(this.args.companyId);
    // Hoisted: the getter builds a Set when @reportTypes is present
    const allowed = this.allowedReportTypes;
    return all.filter(
      (r) =>
        allowed.has(r.reportType) &&
        r.isPublished &&
        !r.isArchived &&
        r.isVisibleToCompany(companyId)
    );
  }

  get reportsByProduct(): Array<{ product: ProductCode; label: string; reports: Report[] }> {
    const reports = this.publishedReports;
    const grouped = new Map<ProductCode, Report[]>();
    for (const report of reports) {
      const rawType = (report.reportType || '').toUpperCase() as ProductCode;
      const product = PRODUCT_LABELS[rawType] ? rawType : 'Core';
      const list = grouped.get(product) ?? [];
      list.push(report);
      grouped.set(product, list);
    }
    return PRODUCT_SECTION_ORDER.filter((code) => grouped.has(code)).map((code) => ({
      product: code,
      label: PRODUCT_LABELS[code],
      reports: grouped.get(code) ?? [],
    }));
  }

  queryFor = (report: Report) => {
    return {
      reportId: report.id,
      companyId: this.args.companyId,
    };
  };

  static MAX_DESCRIPTION_LENGTH = 120;

  truncateDescription = (report: Report) => {
    const desc = report.description;
    if (!desc) return '';
    if (desc.length <= PartnerReportsPage.MAX_DESCRIPTION_LENGTH) return desc;
    return desc.slice(0, PartnerReportsPage.MAX_DESCRIPTION_LENGTH) + '…';
  };

  <template>
    {{#if this.isLoading}}
      <div class="py-8 text-center tio-fg-muted">{{t "reporting.loading"}}</div>
    {{else if this.publishedReports.length}}
      {{#each this.reportsByProduct as |section|}}
        <div class="tio-col-tight">
          <h2 class="tio-text-lg-semibold tio-fg-primary">{{section.label}}</h2>
          {{#each section.reports as |report|}}
            <div
              class="tio-col-extra-tight rounded-lg border tio-border-default tio-bg-surface px-4 py-3 transition hover:tio-border-info hover:shadow-sm"
            >
              {{! Primary Row: Clickable Name }}
              <div class="tio-row">
                <LinkTo
                  class="group tio-row-tight min-w-0 tio-text-sm-medium tio-fg-info-h hover:tio-fg-info"
                  @route={{@viewerRoute}}
                  @query={{this.queryFor report}}
                >
                  <PhTable class="h-4 w-4 shrink-0 tio-fg-info group-hover:tio-fg-info-h" />
                  <span class="truncate">{{report.name}}</span>
                </LinkTo>
              </div>

              {{! Description }}
              {{#let (this.truncateDescription report) as |desc|}}
                {{#if desc}}
                  <p class="pl-6 text-xs tio-fg-secondary">{{desc}}</p>
                {{/if}}
              {{/let}}

              {{! Secondary Row: Date.
                  Note: no usage-stats button here for now — the company-scoped
                  stats endpoint 403s for partner admins (tio-api guard fix
                  pending); re-add once that lands. }}
              <div class="tio-row-loose pl-6 text-xs tio-fg-muted">
                <span>{{t "reporting.updated" date=report.formattedUpdatedAt}}</span>
              </div>
            </div>
          {{/each}}
        </div>
      {{/each}}
    {{else}}
      <div class="py-8 text-center">
        <p class="tio-fg-muted">{{t "reporting.no_reports"}}</p>
      </div>
    {{/if}}
  </template>
}
